import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield, Plus, Trash2, ToggleLeft, ToggleRight, Rss,
  Tag, Send, RefreshCw, Loader2, AlertTriangle, CheckCircle,
  BarChart2, Settings, Cpu, ChevronDown, ChevronUp, BookX, Trash2 as Trash, Ban, Undo2, Users, KeyRound, HardDrive, RefreshCcw, Upload, Bell,
  LayoutDashboard, CalendarDays, Database, DollarSign, TrendingUp,
} from 'lucide-react';
import {
  fetchKeywords, addKeyword, updateKeyword, deleteKeyword,
  fetchSources, addSource, updateSource, deleteSource,
  triggerFetch, triggerReport,
  fetchAdminSettings, saveAdminSettings, fetchUsage, fetchUsageLog,
  fetchDuplicateHints, addDuplicateHint, deleteDuplicateHint,
  fetchReportDates, fetchReportsByDate, fetchReport,
  fetchExcluded, unexcludeArticle,
  fetchUsers, createUser, deleteUser, resetUserPassword, bulkUploadUsers,
  fetchSystemInfo,
  fetchTelegramSubscribers, deleteTelegramSubscriber, toggleTelegramSubscriber, setTelegramSubscriberType,
  fetchNotificationPolicy, updateNotificationPolicy, resetNotificationPolicy,
  fetchAdYears, fetchAdSummary, fetchAdRecords,
} from '../api';

function KeywordsPanel() {
  const qc = useQueryClient();
  const { data: keywords, isLoading } = useQuery({ queryKey: ['keywords'], queryFn: fetchKeywords });

  const [newKw, setNewKw] = useState({ keyword: '', weight: 1.5, category: 'nuclear' });

  const addMut = useMutation({ mutationFn: addKeyword, onSuccess: () => qc.invalidateQueries(['keywords']) });
  const updMut = useMutation({ mutationFn: ({ id, data }) => updateKeyword(id, data), onSuccess: () => qc.invalidateQueries(['keywords']) });
  const delMut = useMutation({ mutationFn: deleteKeyword, onSuccess: () => qc.invalidateQueries(['keywords']) });

  const CATEGORIES = ['nuclear', 'energy', 'general'];
  const CAT_LABEL = { nuclear: '원자력', energy: '에너지', general: '일반' };
  const CAT_COLOR = { nuclear: 'badge-yellow', energy: 'badge-blue', general: 'badge-gray' };

  return (
    <div className="card">
      <h2 className="section-title"><Tag size={16} /> 키워드 관리</h2>
      <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
        가중치가 높을수록 뉴스 관련도 점수가 올라가 상위에 표시됩니다. (0.1 ~ 5.0)
      </p>

      <form
        className="add-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newKw.keyword.trim()) return;
          addMut.mutate(newKw, { onSuccess: () => setNewKw({ keyword: '', weight: 1.5, category: 'nuclear' }) });
        }}
      >
        <input
          className="input"
          placeholder="새 키워드"
          value={newKw.keyword}
          onChange={(e) => setNewKw((p) => ({ ...p, keyword: e.target.value }))}
          style={{ flex: 1 }}
        />
        <input
          className="input"
          type="number"
          step="0.1"
          min="0.1"
          max="5"
          value={newKw.weight}
          onChange={(e) => setNewKw((p) => ({ ...p, weight: parseFloat(e.target.value) }))}
          style={{ width: '80px' }}
          placeholder="가중치"
        />
        <select className="select" value={newKw.category} onChange={(e) => setNewKw((p) => ({ ...p, category: e.target.value }))}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" type="submit" disabled={addMut.isPending}>
          {addMut.isPending ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
          추가
        </button>
      </form>

      {addMut.isError && (
        <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>{addMut.error?.response?.data?.error || '오류'}</div>
      )}

      <div style={{ marginTop: '1rem' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" /></div>
        ) : (
          keywords?.map((kw) => (
            <div key={kw.id} className={`kw-row ${kw.is_active ? '' : 'inactive'}`}>
              <span className={`badge ${CAT_COLOR[kw.category] || 'badge-gray'}`}>{CAT_LABEL[kw.category] || kw.category}</span>
              <span style={{ flex: 1, fontSize: '0.875rem', color: kw.is_active ? '#e2e8f0' : '#475569' }}>{kw.keyword}</span>
              <div className="weight-ctrl">
                <button className="icon-btn small" onClick={() => updMut.mutate({ id: kw.id, data: { weight: Math.max(0.1, +(kw.weight - 0.1).toFixed(1)) } })}>−</button>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8', width: '35px', textAlign: 'center' }}>{kw.weight.toFixed(1)}</span>
                <button className="icon-btn small" onClick={() => updMut.mutate({ id: kw.id, data: { weight: Math.min(5, +(kw.weight + 0.1).toFixed(1)) } })}>+</button>
              </div>
              <button
                className="icon-btn"
                onClick={() => updMut.mutate({ id: kw.id, data: { is_active: !kw.is_active } })}
                title={kw.is_active ? '비활성화' : '활성화'}
              >
                {kw.is_active ? <ToggleRight size={16} color="#4ade80" /> : <ToggleLeft size={16} color="#475569" />}
              </button>
              <button
                className="icon-btn danger"
                onClick={() => { if (window.confirm(`"${kw.keyword}" 삭제?`)) delMut.mutate(kw.id); }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SourcesPanel() {
  const qc = useQueryClient();
  const { data: sources, isLoading } = useQuery({ queryKey: ['sources'], queryFn: fetchSources });
  const [newSrc, setNewSrc] = useState({ name: '', url: '' });

  const addMut = useMutation({ mutationFn: addSource, onSuccess: () => qc.invalidateQueries(['sources']) });
  const updMut = useMutation({ mutationFn: ({ id, data }) => updateSource(id, data), onSuccess: () => qc.invalidateQueries(['sources']) });
  const delMut = useMutation({ mutationFn: deleteSource, onSuccess: () => qc.invalidateQueries(['sources']) });

  return (
    <div className="card">
      <h2 className="section-title"><Rss size={16} /> RSS 소스 관리</h2>

      <form
        className="add-form"
        style={{ flexDirection: 'column', alignItems: 'stretch' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!newSrc.name.trim() || !newSrc.url.trim()) return;
          addMut.mutate(newSrc, { onSuccess: () => setNewSrc({ name: '', url: '' }) });
        }}
      >
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            className="input"
            placeholder="소스 이름"
            value={newSrc.name}
            onChange={(e) => setNewSrc((p) => ({ ...p, name: e.target.value }))}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary btn-sm" type="submit" disabled={addMut.isPending}>
            {addMut.isPending ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} 추가
          </button>
        </div>
        <input
          className="input"
          placeholder="RSS URL (https://...)"
          value={newSrc.url}
          onChange={(e) => setNewSrc((p) => ({ ...p, url: e.target.value }))}
          style={{ marginTop: '0.4rem' }}
        />
      </form>

      {addMut.isError && (
        <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>{addMut.error?.response?.data?.error || '오류'}</div>
      )}

      <div style={{ marginTop: '1rem' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" /></div>
        ) : (
          sources?.map((src) => (
            <div key={src.id} className={`kw-row ${src.is_active ? '' : 'inactive'}`}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.875rem', color: src.is_active ? '#e2e8f0' : '#475569' }}>{src.name}</div>
                <div style={{ fontSize: '0.7rem', color: '#475569', wordBreak: 'break-all' }}>{src.url}</div>
                {src.last_fetched && (
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    마지막 수집: {new Date(src.last_fetched).toLocaleString('ko-KR')} | 총 {src.fetch_count}회
                  </div>
                )}
              </div>
              <button
                className="icon-btn"
                onClick={() => updMut.mutate({ id: src.id, data: { is_active: !src.is_active } })}
              >
                {src.is_active ? <ToggleRight size={16} color="#4ade80" /> : <ToggleLeft size={16} color="#475569" />}
              </button>
              <button
                className="icon-btn danger"
                onClick={() => { if (window.confirm(`"${src.name}" 삭제?`)) delMut.mutate(src.id); }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ControlPanel() {
  const [status, setStatus] = useState(null);
  const [targetType, setTargetType] = useState('all');

  const fetchMut = useMutation({
    mutationFn: triggerFetch,
    onSuccess: (d) => setStatus({ type: 'success', msg: `수집 완료 (${d.count || 0}건 처리)` }),
    onError: () => setStatus({ type: 'error', msg: '수집 실패' }),
  });

  const reportMut = useMutation({
    mutationFn: ({ type, tt }) => triggerReport(type, undefined, tt),
    onSuccess: () => setStatus({ type: 'success', msg: '텔레그램 리포트 전송 완료' }),
    onError: () => setStatus({ type: 'error', msg: '전송 실패' }),
  });

  const fetchAndReportMut = useMutation({
    mutationFn: (tt) => triggerFetchAndReport(tt),
    onSuccess: (d) => setStatus({ type: 'success', msg: `수집 후 즉시 발송 완료 (${d.articles || 0}건)` }),
    onError: () => setStatus({ type: 'error', msg: '전송 실패' }),
  });

  const SELECT_STYLE = {
    background: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
    color: '#e2e8f0', padding: '5px 10px', fontSize: '0.82rem', cursor: 'pointer',
  };
  const TARGET_OPTIONS = [
    { value: 'all',    label: '📢 전체 (일반 + PRO)' },
    { value: 'pro',    label: '⭐ PRO만' },
    { value: 'normal', label: '👤 일반만' },
  ];

  return (
    <div className="card">
      <h2 className="section-title"><Shield size={16} /> 수동 제어</h2>

      {status && (
        <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '1rem' }}>
          {status.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          {status.msg}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <button
          className="btn btn-primary"
          onClick={() => { setStatus(null); fetchMut.mutate(); }}
          disabled={fetchMut.isPending}
        >
          {fetchMut.isPending ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
          지금 바로 뉴스 수집 (발송 없음)
        </button>

        <hr className="divider" />

        {/* 발송 대상 선택 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>발송 대상:</span>
          <select value={targetType} onChange={e => setTargetType(e.target.value)} style={SELECT_STYLE}>
            {TARGET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* 수집 후 즉시 발송 */}
        <button
          className="btn btn-success"
          onClick={() => { setStatus(null); fetchAndReportMut.mutate(targetType); }}
          disabled={fetchAndReportMut.isPending || reportMut.isPending}
        >
          {fetchAndReportMut.isPending ? <Loader2 size={15} className="spin" /> : <Send size={15} />}
          지금 수집 후 즉시 발송
        </button>

        {/* 당일 기사로만 리포트 */}
        <button
          className="btn btn-success btn-sm"
          style={{ background: '#1e3a5f' }}
          onClick={() => { setStatus(null); reportMut.mutate({ type: 'today', tt: targetType }); }}
          disabled={reportMut.isPending || fetchAndReportMut.isPending}
        >
          {reportMut.isPending ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
          당일 리포트만 발송 (수집 없이)
        </button>
      </div>
    </div>
  );
}

function UsagePanel() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-usage'], queryFn: fetchUsage, refetchInterval: 30000 });
  const [showLog, setShowLog] = useState(false);
  const { data: logData } = useQuery({
    queryKey: ['usage-log'],
    queryFn: () => fetchUsageLog({ limit: 50 }),
    enabled: showLog,
  });

  if (isLoading) return <div className="card empty-state"><div className="spinner" /></div>;

  const total = data?.current?.total || {};
  const history = data?.history || [];
  const daily = data?.current?.daily || [];
  const byAction = data?.current?.byAction || [];
  const period = data?.billingPeriod || {};

  const totalCostKrw = ((total.cost_usd || 0) * 1380).toFixed(0);

  return (
    <div className="card">
      <h2 className="section-title"><BarChart2 size={16} /> AI 토큰 사용량</h2>
      <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '1rem' }}>
        결제 주기: 매월 {data?.billingDay}일 · 현재 기간: {period.start} ~ {period.end}
      </div>

      <div className="usage-summary">
        <div className="usage-card">
          <div className="usage-label">이번 달 총 비용</div>
          <div className="usage-value cost">${(total.cost_usd || 0).toFixed(4)}</div>
          <div className="usage-sub">≈ ₩{parseInt(totalCostKrw).toLocaleString()}</div>
        </div>
        <div className="usage-card">
          <div className="usage-label">입력 토큰</div>
          <div className="usage-value">{(total.input_tokens || 0).toLocaleString()}</div>
          <div className="usage-sub">tokens</div>
        </div>
        <div className="usage-card">
          <div className="usage-label">출력 토큰</div>
          <div className="usage-value">{(total.output_tokens || 0).toLocaleString()}</div>
          <div className="usage-sub">tokens</div>
        </div>
        <div className="usage-card">
          <div className="usage-label">호출 횟수</div>
          <div className="usage-value">{(total.count || 0).toLocaleString()}</div>
          <div className="usage-sub">건</div>
        </div>
      </div>

      {byAction.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', fontWeight: 600 }}>액션별 분류</div>
          <table className="usage-table">
            <thead><tr><th>액션</th><th>모델</th><th>입력</th><th>출력</th><th>비용(USD)</th><th>건수</th></tr></thead>
            <tbody>
              {byAction.map((r, i) => (
                <tr key={i}>
                  <td>{r.action}</td>
                  <td style={{ color: '#64748b', fontSize: '0.75rem' }}>{r.model}</td>
                  <td>{(r.input_tokens || 0).toLocaleString()}</td>
                  <td>{(r.output_tokens || 0).toLocaleString()}</td>
                  <td style={{ color: '#fbbf24' }}>${(r.cost_usd || 0).toFixed(5)}</td>
                  <td>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', fontWeight: 600 }}>월별 이력 (최근 12개월)</div>
          <table className="usage-table">
            <thead><tr><th>월</th><th>입력 토큰</th><th>출력 토큰</th><th>비용(USD)</th><th>≈ KRW</th><th>건수</th></tr></thead>
            <tbody>
              {history.map((r, i) => (
                <tr key={i}>
                  <td>{r.month}</td>
                  <td>{(r.input_tokens || 0).toLocaleString()}</td>
                  <td>{(r.output_tokens || 0).toLocaleString()}</td>
                  <td style={{ color: '#fbbf24' }}>${(r.cost_usd || 0).toFixed(4)}</td>
                  <td style={{ color: '#94a3b8' }}>₩{Math.round((r.cost_usd || 0) * 1380).toLocaleString()}</td>
                  <td>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={() => setShowLog((v) => !v)}>
        {showLog ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        상세 호출 로그 {showLog ? '접기' : '보기'}
      </button>

      {showLog && logData?.logs && (
        <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
          <table className="usage-table" style={{ fontSize: '0.72rem' }}>
            <thead><tr><th>시각</th><th>액션</th><th>기사</th><th>입력</th><th>출력</th><th>USD</th></tr></thead>
            <tbody>
              {logData.logs.map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.created_at?.slice(0, 16)}</td>
                  <td>{r.action}</td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={r.article_title}>{r.article_title || '-'}</td>
                  <td>{r.input_tokens}</td>
                  <td>{r.output_tokens}</td>
                  <td>${(r.cost_usd || 0).toFixed(5)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total.count === 0 && (
        <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '0.75rem' }}>
          이번 결제 주기에 AI 사용 이력이 없습니다. Claude API 키 설정 후 번역 기능을 사용하면 자동 기록됩니다.
        </div>
      )}
    </div>
  );
}

function SettingsPanel() {
  const qc = useQueryClient();
  const { data: settings, isLoading } = useQuery({ queryKey: ['admin-settings'], queryFn: fetchAdminSettings });
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (settings && !form) {
      setForm({ BILLING_DAY: settings.BILLING_DAY || '1', PUBLISHED_HOURS_FILTER: settings.PUBLISHED_HOURS_FILTER || '48' });
    }
  }, [settings]);

  const saveMut = useMutation({
    mutationFn: saveAdminSettings,
    onSuccess: () => { setSaved(true); qc.invalidateQueries(['admin-settings']); setTimeout(() => setSaved(false), 2500); },
  });

  if (isLoading || !form) return <div className="card empty-state"><div className="spinner" /></div>;

  return (
    <div className="card">
      <h2 className="section-title"><Settings size={16} /> 환경설정</h2>

      <div className="settings-grid">
        <div className="settings-section">
          <div className="settings-label">📅 결제일 (매월 N일)</div>
          <div className="settings-desc">AI 토큰 사용량 집계 기준일입니다.</div>
          <input className="input" type="number" min="1" max="28" value={form.BILLING_DAY}
            onChange={(e) => setForm((p) => ({ ...p, BILLING_DAY: e.target.value }))} style={{ width: '80px', marginTop: '0.4rem' }} />
        </div>

        <div className="settings-section">
          <div className="settings-label">⏰ 발행일 필터 기준 (시간)</div>
          <div className="settings-desc">이 시간 이내에 발행된 기사만 표시합니다. 기본값 48시간.</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
            <input className="input" type="number" min="1" max="168" value={form.PUBLISHED_HOURS_FILTER}
              onChange={(e) => setForm((p) => ({ ...p, PUBLISHED_HOURS_FILTER: e.target.value }))} style={{ width: '80px' }} />
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>시간 이내</span>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-label">🤖 Claude AI 번역</div>
          <div className="settings-desc">
            상태: <span style={{ color: settings?.AI_ENABLED ? '#4ade80' : '#f87171', fontWeight: 600 }}>
              {settings?.AI_ENABLED ? '✅ 활성화됨' : '❌ 비활성 (API 키 없음)'}
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.3rem' }}>
            활성화: <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px' }}>backend/.env</code>에 <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px' }}>ANTHROPIC_API_KEY=sk-ant-...</code> 입력 후 재시작
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-label">📡 텔레그램</div>
          <div className="settings-desc">
            상태: <span style={{ color: settings?.TELEGRAM_ENABLED ? '#4ade80' : '#f87171', fontWeight: 600 }}>
              {settings?.TELEGRAM_ENABLED ? '✅ 연결됨' : '❌ 미설정'}
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.3rem' }}>
            웹 URL: <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px' }}>{settings?.WEB_URL || '(미설정)'}</code>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-label">🔑 관리자 비밀번호</div>
          <div className="settings-desc">변경은 <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px' }}>backend/.env</code>의 <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px' }}>ADMIN_PASSWORD</code> 값을 수정하세요.</div>
        </div>
      </div>

      <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button className="btn btn-primary btn-sm" onClick={() => saveMut.mutate(form)} disabled={saveMut.isPending}>
          {saveMut.isPending ? <Loader2 size={13} className="spin" /> : <CheckCircle size={13} />} 저장
        </button>
        {saved && <span style={{ color: '#4ade80', fontSize: '0.85rem' }}>✅ 저장되었습니다</span>}
      </div>
    </div>
  );
}

const TYPE_LABEL = { domestic: '국내', overseas: '해외(국문)', column: '칼럼', foreign: '영문' };

function DuplicateHintsPanel() {
  const qc = useQueryClient();

  // 날짜 / 리포트 선택
  const { data: dates = [] } = useQuery({ queryKey: ['report-dates'], queryFn: fetchReportDates });
  const [selDate, setSelDate] = useState('');
  const { data: reportsOfDay = [] } = useQuery({
    queryKey: ['reports-by-date', selDate],
    queryFn: () => fetchReportsByDate(selDate),
    enabled: !!selDate,
  });
  const [selReportId, setSelReportId] = useState(null);
  const { data: reportDetail } = useQuery({
    queryKey: ['report-detail', selReportId],
    queryFn: () => fetchReport(selReportId),
    enabled: !!selReportId,
  });

  // 날짜 초기값
  React.useEffect(() => { if (dates.length && !selDate) setSelDate(dates[0].date); }, [dates]);
  React.useEffect(() => { if (reportsOfDay.length) setSelReportId(reportsOfDay[0].id); }, [reportsOfDay]);

  // 체크박스 선택
  const [checked, setChecked] = useState({});
  const toggleCheck = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));
  const selectedIds = Object.keys(checked).filter((k) => checked[k]).map(Number);

  // 등록
  const [results, setResults] = useState([]);
  const addMut = useMutation({
    mutationFn: ({ a, b }) => addDuplicateHint(a, b),
    onSuccess: (data) => {
      setResults((p) => [...p, { ok: true, msg: `✅ 등록: "${data.title_b?.slice(0, 35)}..."` }]);
      qc.invalidateQueries(['dup-hints']);
    },
    onError: (e) => setResults((p) => [...p, { ok: false, msg: `❌ ${e.response?.data?.error || '오류'}` }]),
  });

  async function handleRegister() {
    if (selectedIds.length < 2) return;
    setResults([]);
    const [base, ...dups] = selectedIds;
    for (const dupId of dups) await addMut.mutateAsync({ a: base, b: dupId });
    setChecked({});
  }

  // 등록된 힌트 목록
  const { data: hints = [], isLoading: hintsLoading } = useQuery({ queryKey: ['dup-hints'], queryFn: fetchDuplicateHints });
  const delMut = useMutation({ mutationFn: deleteDuplicateHint, onSuccess: () => qc.invalidateQueries(['dup-hints']) });

  const reportArticles = reportDetail?.articles || [];

  function formatReportLabel(r) {
    const time = r.created_at?.slice(11, 16) || '';
    const type = r.report_type?.replace('regular:', '').replace('📊 ', '').replace('🌙 ', '').replace('📅 ', '') || '';
    return `${time} — ${type.slice(0, 30)}`;
  }

  return (
    <div>
      {/* 상단: 리포트 기반 선택 */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 className="section-title"><BookX size={16} /> 중복 패턴 학습</h2>
        <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '1rem', lineHeight: 1.6 }}>
          텔레그램으로 발송된 리포트에서 같은 내용의 기사를 <strong>2개 이상 선택</strong>하여 등록하세요.<br />
          첫 번째 선택이 기준(남길 기사), 나머지는 중복으로 처리됩니다.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.25rem' }}>날짜</div>
            <select className="select" value={selDate} onChange={(e) => { setSelDate(e.target.value); setSelReportId(null); setChecked({}); }}>
              {dates.map((d) => <option key={d.date} value={d.date}>{d.date} ({d.count}건)</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.25rem' }}>발송 회차</div>
            <select className="select" value={selReportId || ''} onChange={(e) => { setSelReportId(Number(e.target.value)); setChecked({}); }}>
              {reportsOfDay.map((r) => (
                <option key={r.id} value={r.id}>{formatReportLabel(r)}</option>
              ))}
            </select>
          </div>
          {selectedIds.length >= 2 && (
            <button className="btn btn-primary btn-sm" onClick={handleRegister} disabled={addMut.isPending}>
              {addMut.isPending ? <Loader2 size={13} className="spin" /> : <Plus size={13} />}
              선택 {selectedIds.length}건 중복 등록
            </button>
          )}
          {selectedIds.length === 1 && (
            <span style={{ fontSize: '0.78rem', color: '#64748b', alignSelf: 'center' }}>중복 기사를 1개 이상 더 선택하세요</span>
          )}
        </div>

        {results.length > 0 && (
          <div style={{ marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {results.map((r, i) => <div key={i} style={{ fontSize: '0.8rem', color: r.ok ? '#4ade80' : '#f87171' }}>{r.msg}</div>)}
          </div>
        )}

        {/* 기사 목록 */}
        {!selReportId ? (
          <div style={{ color: '#475569', fontSize: '0.85rem' }}>날짜와 발송 회차를 선택하세요.</div>
        ) : reportArticles.length === 0 ? (
          <div style={{ color: '#475569', fontSize: '0.85rem' }}>해당 리포트에 기사 정보가 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {reportArticles.map((a, i) => {
              const isChecked = !!checked[a.id];
              const order = selectedIds.indexOf(a.id);
              return (
                <div key={a.id}
                  onClick={() => toggleCheck(a.id)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                    padding: '0.55rem 0.7rem', borderRadius: '7px', cursor: 'pointer',
                    border: `1px solid ${isChecked ? '#6366f1' : '#1e293b'}`,
                    background: isChecked ? '#1e1b4b' : '#0f172a',
                    transition: 'all 0.12s',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: '4px', flexShrink: 0, marginTop: 1,
                    background: isChecked ? '#6366f1' : '#1e293b',
                    border: `1px solid ${isChecked ? '#818cf8' : '#334155'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', color: '#fff', fontWeight: 700,
                  }}>
                    {isChecked ? (order === 0 ? '기준' : order + 1) : ''}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                      <span style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: '3px', background: '#1e293b', color: '#64748b' }}>
                        {TYPE_LABEL[a.article_type] || a.article_type}
                      </span>
                      <span style={{ fontSize: '0.65rem', color: '#475569' }}>{a.source}</span>
                      {a.is_personnel === 1 && <span style={{ fontSize: '0.65rem', color: '#fb923c' }}>🔔 인사</span>}
                      {a.is_duplicate === 1 && <span style={{ fontSize: '0.65rem', color: '#64748b' }}>이미 중복</span>}
                      <span style={{ fontSize: '0.65rem', marginLeft: 'auto', display: 'flex', gap: '0.6rem' }}>
                        <span style={{ color: '#94a3b8' }}>★{a.relevance_score?.toFixed(1)}</span>
                        <span style={{ color: '#64748b' }}>#{a.id}</span>
                      </span>
                    </div>
                    <a href={a.link} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '0.875rem', color: isChecked ? '#a5b4fc' : '#e2e8f0', lineHeight: 1.4, display: 'block' }}>
                      {a.title}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 하단: 등록된 힌트 목록 */}
      <div className="card">
        <h2 className="section-title" style={{ marginBottom: '0.75rem' }}>등록된 중복 패턴</h2>
        {hintsLoading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : hints.length === 0 ? (
          <div style={{ color: '#475569', fontSize: '0.85rem' }}>아직 등록된 패턴이 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '360px', overflowY: 'auto' }}>
            {hints.map((h) => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.45rem 0.6rem', background: '#0f172a', borderRadius: '6px', border: '1px solid #1e293b' }}>
                <div style={{ flex: 1, fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.55 }}>
                  <span style={{ color: '#334155', fontSize: '0.67rem' }}>{h.created_at?.slice(0, 16)}</span>
                  <div style={{ color: '#cbd5e1', marginTop: '0.15rem' }}>{h.norm_a}</div>
                  <div style={{ color: '#475569' }}>↕ 중복</div>
                  <div style={{ color: '#94a3b8' }}>{h.norm_b}</div>
                </div>
                <button className="icon-btn" style={{ flexShrink: 0, marginTop: 2 }} onClick={() => delMut.mutate(h.id)} title="패턴 삭제">
                  <Trash size={13} color="#f87171" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ExcludedPanel() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['excluded'], queryFn: fetchExcluded });
  const unexMut = useMutation({
    mutationFn: (id) => unexcludeArticle(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['excluded'] }); qc.invalidateQueries({ queryKey: ['articles'] }); },
  });
  return (
    <div className="card">
      <h2 className="section-title"><Ban size={15} color="#ef4444" /> 배제된 기사 목록</h2>
      <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.75rem' }}>
        배제된 기사는 뉴스·날짜별 조회에서 숨겨집니다. 해제하면 다시 표시됩니다.
      </p>
      {isLoading ? <div className="empty-state"><Loader2 size={18} className="spin" /></div>
        : rows.length === 0 ? <div style={{ color: '#475569', fontSize: '0.85rem' }}>배제된 기사가 없습니다.</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {rows.map((r) => (
              <div key={r.article_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.6rem', background: '#0f172a', borderRadius: '6px', border: '1px solid #1e293b' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', color: '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title || `기사 #${r.article_id}`}</div>
                  <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.1rem' }}>
                    {r.source && <span style={{ marginRight: '0.5rem' }}>{r.source}</span>}
                    {r.excluded_at?.slice(0, 16)} · {r.reason}
                  </div>
                </div>
                <button className="icon-btn" onClick={() => unexMut.mutate(r.article_id)} title="배제 해제">
                  <Undo2 size={13} color="#34d399" />
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function UsersPanel() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ['admin-users'], queryFn: fetchUsers });
  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [msg, setMsg] = useState('');

  const createMut = useMutation({
    mutationFn: () => createUser({ username: newUsername.trim(), role: newRole }),
    onSuccess: () => { setMsg(`✅ '${newUsername}' 생성 완료 (초기 비밀번호: 1234)`); setNewUsername(''); qc.invalidateQueries(['admin-users']); },
    onError: e => setMsg(`❌ ${e.response?.data?.error || '오류'}`),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: () => { setMsg('✅ 삭제 완료'); qc.invalidateQueries(['admin-users']); },
    onError: e => setMsg(`❌ ${e.response?.data?.error || '오류'}`),
  });

  const resetMut = useMutation({
    mutationFn: (id) => resetUserPassword(id),
    onSuccess: () => setMsg('✅ 비밀번호를 1234로 초기화했습니다.'),
    onError: e => setMsg(`❌ ${e.response?.data?.error || '오류'}`),
  });

  return (
    <div className="card">
      <h2 className="section-title"><Users size={15} /> 유저 관리</h2>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>아이디</div>
          <input className="input" placeholder="새 유저 아이디" value={newUsername} onChange={e => setNewUsername(e.target.value)}
            style={{ width: '160px' }} />
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>역할</div>
          <select value={newRole} onChange={e => setNewRole(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', padding: '5px 8px', fontSize: '0.85rem' }}>
            <option value="user">일반 유저</option>
            <option value="master">마스터</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setMsg(''); createMut.mutate(); }} disabled={!newUsername.trim() || createMut.isPending}>
          <Plus size={13} /> 생성 (초기 비번: 1234)
        </button>
      </div>

      {msg && <div style={{ fontSize: '0.8rem', color: msg.startsWith('✅') ? '#4ade80' : '#f87171', marginBottom: '0.75rem' }}>{msg}</div>}

      {/* 일괄 업로드 */}
      <div style={{ borderTop: '1px solid #1e293b', paddingTop: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Upload size={12} /> CSV/엑셀 일괄 업로드
          <span style={{ fontSize: '0.68rem', color: '#334155' }}>— 컬럼: <code style={{color:'#94a3b8'}}>username</code>, <code style={{color:'#94a3b8'}}>role</code>(선택)</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '5px 12px', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer' }}>
            <Upload size={13} /> 파일 선택
            <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                setMsg('');
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const r = await bulkUploadUsers(fd);
                  setMsg(`✅ ${r.created}명 생성, ${r.skipped}명 중복 건너뜀 (전체 ${r.total}명)`);
                  qc.invalidateQueries(['admin-users']);
                } catch (err) {
                  setMsg(`❌ ${err.response?.data?.error || '업로드 실패'}`);
                }
                e.target.value = '';
              }}
            />
          </label>
          <a href="data:text/csv;charset=utf-8,username%2Crole%0Auser001%2Cuser%0Auser002%2Cuser" download="users_template.csv"
            style={{ fontSize: '0.72rem', color: '#475569', textDecoration: 'underline' }}>템플릿 다운로드</a>
        </div>
      </div>

      {isLoading ? <div style={{ color: '#475569' }}>불러오는 중...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {users.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0', borderBottom: '1px solid #1e293b' }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '0.875rem', color: '#e2e8f0', fontWeight: 600 }}>{u.username}</span>
                <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', padding: '1px 6px', borderRadius: '3px',
                  background: u.role === 'master' ? '#1d4ed8' : '#1e293b', color: u.role === 'master' ? '#fff' : '#64748b' }}>
                  {u.role === 'master' ? 'MASTER' : 'USER'}
                </span>
                {u.must_change_password && <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', color: '#fbbf24' }}>비번변경필요</span>}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#334155' }}>{u.last_login ? u.last_login.slice(0,16) + ' 로그인' : '미접속'}</div>
              <button className="icon-btn small" title="비밀번호 초기화 (1234)" onClick={() => { setMsg(''); resetMut.mutate(u.id); }}>
                <KeyRound size={12} />
              </button>
              {u.role !== 'master' && (
                <button className="icon-btn small danger" title="삭제" onClick={() => { if(window.confirm(`'${u.username}' 삭제?`)) { setMsg(''); deleteMut.mutate(u.id); } }}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmt(bytes) {
  if (!bytes) return '0 B';
  const u = ['B','KB','MB','GB'];
  let i = 0, v = bytes;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function GaugeBar({ percent, color = '#6366f1' }) {
  return (
    <div style={{ height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden', margin: '4px 0 8px' }}>
      <div style={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: percent > 85 ? '#f87171' : percent > 60 ? '#fbbf24' : color, borderRadius: '4px', transition: 'width 0.5s' }} />
    </div>
  );
}

function SystemPanel() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ['system-info'], queryFn: fetchSystemInfo, refetchInterval: 30000 });

  if (isLoading) return <div className="card" style={{ color: '#475569' }}>불러오는 중...</div>;
  if (!data) return null;

  const { disk, mem, dbSize, articleCount, bkCount, freeTierDiskGB } = data;
  const freeTierBytes = freeTierDiskGB * 1024 * 1024 * 1024;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* 디스크 */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 className="section-title" style={{ marginBottom: 0 }}><HardDrive size={15} /> 디스크 사용량</h2>
          <button onClick={refetch} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: '2px' }}><RefreshCcw size={13} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
          {[
            { label: '사용 중', value: fmt(disk.used), sub: `${disk.percent}%`, color: disk.percent > 85 ? '#f87171' : '#e2e8f0' },
            { label: '여유 공간', value: fmt(disk.free), sub: '', color: '#4ade80' },
            { label: '전체 (서버)', value: fmt(disk.total), sub: `Free Tier: ${freeTierDiskGB}GB`, color: '#94a3b8' },
          ].map(s => (
            <div key={s.label} style={{ background: '#0f172a', borderRadius: '8px', padding: '0.75rem', border: '1px solid #1e293b', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>{s.label}</div>
              {s.sub && <div style={{ fontSize: '0.65rem', color: '#475569' }}>{s.sub}</div>}
            </div>
          ))}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '2px' }}>서버 디스크 ({disk.percent}%)</div>
        <GaugeBar percent={disk.percent} color="#6366f1" />
        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '2px' }}>Oracle Free Tier 기준 50GB 대비 ({Math.round((disk.used / freeTierBytes) * 100)}%)</div>
        <GaugeBar percent={Math.round((disk.used / freeTierBytes) * 100)} color="#38bdf8" />
      </div>

      {/* 메모리 */}
      <div className="card">
        <h2 className="section-title"><Cpu size={15} /> 메모리 사용량</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
          {[
            { label: '사용 중', value: fmt(mem.used), color: mem.percent > 85 ? '#f87171' : '#e2e8f0' },
            { label: '여유', value: fmt(mem.free), color: '#4ade80' },
            { label: '전체 (1GB)', value: fmt(mem.total), color: '#94a3b8' },
          ].map(s => (
            <div key={s.label} style={{ background: '#0f172a', borderRadius: '8px', padding: '0.75rem', border: '1px solid #1e293b', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '2px' }}>메모리 ({mem.percent}%)</div>
        <GaugeBar percent={mem.percent} color="#a78bfa" />
      </div>

      {/* DB/데이터 */}
      <div className="card">
        <h2 className="section-title"><BarChart2 size={15} /> 데이터 현황</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
          {[
            { label: 'DB 파일 크기', value: fmt(dbSize), color: '#fbbf24' },
            { label: '수집 기사 수', value: articleCount.toLocaleString() + '건', color: '#38bdf8' },
            { label: '빅카인즈 아카이브', value: bkCount.toLocaleString() + '건', color: '#4ade80' },
          ].map(s => (
            <div key={s.label} style={{ background: '#0f172a', borderRadius: '8px', padding: '0.75rem', border: '1px solid #1e293b', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const MSG_LABEL = {
  regular_update: '정기 업데이트',
  daily_report: '일일/주간 리포트',
  knf_press_alert: 'KNF 보도자료 즉시 알림',
  emergency_alert: '긴급속보',
  executive_watch: 'Executive Watch',
  master_alert: '마스터 알림',
};
const SUB_LABEL = { normal: '일반', pro: 'Pro', master: '마스터' };
const SUB_ORDER = ['normal', 'pro', 'master'];
const MSG_ORDER = ['regular_update', 'daily_report', 'knf_press_alert', 'emergency_alert', 'executive_watch', 'master_alert'];

function NotificationPolicyPanel() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['notification-policy'], queryFn: fetchNotificationPolicy });
  const updMut = useMutation({ mutationFn: updateNotificationPolicy, onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-policy'] }) });
  const resetMut = useMutation({ mutationFn: resetNotificationPolicy, onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-policy'] }) });

  if (isLoading) return <div className="card" style={{ color: '#94a3b8' }}>로딩 중...</div>;

  const state = new Map();
  for (const r of rows) state.set(`${r.subscriber_type}:${r.message_type}`, !!r.enabled);

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h2 className="section-title" style={{ margin: 0 }}>알림 정책</h2>
        <button className="btn btn-sm btn-secondary" onClick={() => resetMut.mutate()} disabled={resetMut.isPending}>기본값으로 초기화</button>
      </div>
      <p style={{ fontSize: '0.83rem', color: '#94a3b8', marginBottom: '1rem' }}>구독자 등급별 메시지 수신 여부를 ON/OFF 합니다.</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155', color: '#94a3b8' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>메시지 종류</th>
              {SUB_ORDER.map(st => <th key={st} style={{ textAlign: 'center', padding: '0.5rem' }}>{SUB_LABEL[st]}</th>)}
            </tr>
          </thead>
          <tbody>
            {MSG_ORDER.map(mt => (
              <tr key={mt} style={{ borderBottom: '1px solid #1e293b' }}>
                <td style={{ padding: '0.5rem', color: '#e2e8f0' }}>{MSG_LABEL[mt]}</td>
                {SUB_ORDER.map(st => {
                  const enabled = state.get(`${st}:${mt}`);
                  return (
                    <td key={st} style={{ textAlign: 'center', padding: '0.5rem' }}>
                      <button
                        className="icon-btn"
                        title={enabled ? '끄기' : '켜기'}
                        onClick={() => updMut.mutate({ message_type: mt, subscriber_type: st, enabled: enabled ? 0 : 1 })}
                        disabled={updMut.isPending}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        {enabled ? <ToggleRight size={20} color="#4ade80" /> : <ToggleLeft size={20} color="#64748b" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TelegramPanel() {
  const qc = useQueryClient();
  const { data: subs = [], isLoading, refetch } = useQuery({ queryKey: ['tg-subs'], queryFn: fetchTelegramSubscribers });
  const delMut = useMutation({ mutationFn: deleteTelegramSubscriber, onSuccess: () => qc.invalidateQueries(['tg-subs']) });
  const togMut = useMutation({ mutationFn: toggleTelegramSubscriber, onSuccess: () => qc.invalidateQueries(['tg-subs']) });
  const typeMut = useMutation({ mutationFn: ({ id, type }) => setTelegramSubscriberType(id, type), onSuccess: () => qc.invalidateQueries(['tg-subs']) });

  const BOT_USERNAME = 'KNFNM_bot';
  const botDeepLink = `https://t.me/${BOT_USERNAME}?start=join`;
  const joinUrl = `https://newsmonitor-volcof.duckdns.org/join`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(joinUrl)}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* QR 카드 */}
      <div className="card" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ textAlign: 'center' }}>
          <img src={qrUrl} alt="텔레그램 구독 QR" style={{ width: 160, height: 160, borderRadius: 8, background: '#fff', padding: 4 }} />
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.4rem' }}>QR 스캔 → 봇 구독</div>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 className="section-title"><Bell size={16} /> 텔레그램 알림 구독</h2>
          <p style={{ fontSize: '0.83rem', color: '#94a3b8', marginBottom: '0.75rem', lineHeight: 1.6 }}>
            QR을 오프라인에서 허가된 사용자에게만 공유하세요.<br />
            텔레그램 설치 안내 → 봇 연결까지 자동으로 안내됩니다.<br />
            구독 해제는 봇에서 <b style={{color:'#f87171'}}>/stop</b> 전송
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <a href={joinUrl} target="_blank" rel="noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.35rem 0.8rem', background:'#0ea5e9', color:'#fff', borderRadius:6, fontSize:'0.8rem', textDecoration:'none' }}>
              <Bell size={13} /> 구독 페이지 열기
            </a>
            <a href={botDeepLink} target="_blank" rel="noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.35rem 0.8rem', background:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:6, fontSize:'0.8rem', textDecoration:'none' }}>
              봇 직접 열기
            </a>
            <button className="btn btn-sm btn-secondary" onClick={() => refetch()}>
              <RefreshCw size={12} /> 새로고침
            </button>
          </div>
          <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#0f172a', borderRadius: 6, border: '1px solid #1e293b' }}>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.2rem' }}>구독 페이지 URL (QR에 포함)</div>
            <code style={{ fontSize: '0.75rem', color: '#38bdf8', wordBreak: 'break-all' }}>{joinUrl}</code>
          </div>
        </div>
      </div>

      {/* 구독자 목록 */}
      <div className="card">
        <h2 className="section-title"><Users size={16} /> 구독자 목록 ({subs.length}명)</h2>
        {isLoading ? (
          <div style={{ color: '#64748b', fontSize: '0.85rem' }}>로딩 중...</div>
        ) : subs.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '1rem 0' }}>아직 구독자가 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {subs.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.6rem', background: '#0f172a', borderRadius: 7, border: `1px solid ${s.is_active ? '#1e3a5f' : '#1e293b'}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', color: s.is_active ? '#e2e8f0' : '#475569', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {s.first_name || '(이름없음)'}
                    {s.username && <span style={{ fontSize: '0.75rem', color: '#38bdf8' }}>@{s.username}</span>}
                    {s.is_active ? (
                      <span style={{ fontSize: '0.65rem', background: '#14532d', color: '#4ade80', padding: '1px 5px', borderRadius: 4 }}>활성</span>
                    ) : (
                      <span style={{ fontSize: '0.65rem', background: '#1e293b', color: '#64748b', padding: '1px 5px', borderRadius: 4 }}>비활성</span>
                    )}
                    {s.subscriber_type === 'pro' ? (
                      <span style={{ fontSize: '0.65rem', background: '#4c1d95', color: '#c4b5fd', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>PRO</span>
                    ) : (
                      <span style={{ fontSize: '0.65rem', background: '#1e293b', color: '#64748b', padding: '1px 6px', borderRadius: 4 }}>일반</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: '0.1rem' }}>
                    ID: {s.chat_id} · 등록: {s.registered_at?.slice(0,10)}
                    {s.last_seen && ` · 최근: ${s.last_seen?.slice(0,10)}`}
                  </div>
                </div>
                <button
                  title={s.subscriber_type === 'pro' ? 'PRO → 일반으로 변경' : '일반 → PRO로 변경'}
                  onClick={() => typeMut.mutate({ id: s.id, type: s.subscriber_type === 'pro' ? 'normal' : 'pro' })}
                  style={{ padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer', background: s.subscriber_type === 'pro' ? '#4c1d95' : '#1e293b', color: s.subscriber_type === 'pro' ? '#c4b5fd' : '#64748b' }}>
                  {s.subscriber_type === 'pro' ? 'PRO' : '일반'}
                </button>
                <button className="icon-btn" title={s.is_active ? '비활성화' : '활성화'} onClick={() => togMut.mutate(s.id)}>
                  {s.is_active ? <ToggleRight size={16} color="#4ade80" /> : <ToggleLeft size={16} />}
                </button>
                <button className="icon-btn danger" title="삭제" onClick={() => { if(window.confirm(`${s.first_name || s.chat_id} 구독자를 삭제하시겠습니까?`)) delMut.mutate(s.id); }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <NotificationPolicyPanel />
    </div>
  );
}

function AdAnalysisPanel() {
  const { data: allYears = [] } = useQuery({ queryKey: ['ad-years'], queryFn: fetchAdYears });
  const [selectedYears, setSelectedYears] = useState([]);
  const [searchMedia, setSearchMedia] = useState('');
  const [tab, setTab] = useState('summary');

  const activeYears = selectedYears.length ? selectedYears : allYears;

  const { data: summary, isLoading: sumLoading } = useQuery({
    queryKey: ['ad-summary', activeYears],
    queryFn: () => fetchAdSummary(activeYears),
    enabled: activeYears.length > 0,
  });

  const { data: records, isLoading: recLoading } = useQuery({
    queryKey: ['ad-records', activeYears, searchMedia],
    queryFn: () => fetchAdRecords({ years: activeYears.join(','), media_name: searchMedia || undefined, limit: 100 }),
    enabled: activeYears.length > 0 && tab === 'detail',
  });

  const toggleYear = (y) => {
    setSelectedYears(prev =>
      prev.includes(y) ? prev.filter(x => x !== y) : [...prev, y].sort((a,b) => a-b)
    );
  };

  const fmtAmount = (v) => v ? `${(v/1000).toFixed(0)}백만` : '-';
  const fmtAmountRaw = (v) => v ? `${v.toLocaleString()}천원` : '-';

  return (
    <div>
      {/* 연도 선택 */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="section-title"><DollarSign size={15} /> 광고비 분석 — 연도 선택</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
          <button
            onClick={() => setSelectedYears([])}
            style={{ padding: '0.3rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: selectedYears.length === 0 ? '#6366f1' : '#1e293b', color: '#e2e8f0', fontSize: '0.8rem' }}
          >전체</button>
          {allYears.map(y => (
            <button key={y} onClick={() => toggleYear(y)}
              style={{ padding: '0.3rem 0.6rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                background: selectedYears.includes(y) ? '#6366f1' : '#1e293b', color: '#e2e8f0' }}>
              {y}
            </button>
          ))}
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#64748b' }}>
          선택: {activeYears.length === allYears.length ? '전체 연도' : activeYears.join(', ')} ({activeYears.length}개년)
        </div>
      </div>

      {/* 서브탭 */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[['summary','종합 집계'],['detail','상세 목록'],['match','매칭']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '0.4rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
              background: tab === k ? '#6366f1' : '#1e293b', color: '#e2e8f0' }}>{l}</button>
        ))}
      </div>

      {tab === 'summary' && (
        <>
          {sumLoading ? <div style={{ color: '#64748b', padding: '1rem' }}>로딩 중...</div> : summary && (
            <>
              {/* 연도별 추이 */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="section-title"><TrendingUp size={14} /> 연도별 광고비 추이</div>
                <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                  <table className="usage-table">
                    <thead><tr>
                      <th>연도</th><th>매체수</th><th>집행건수</th><th>총 광고비</th>
                    </tr></thead>
                    <tbody>
                      {(summary.byYear || []).map(r => (
                        <tr key={r.year}>
                          <td style={{ fontWeight: 600 }}>{r.year}</td>
                          <td>-</td>
                          <td>{r.count}건</td>
                          <td style={{ color: '#fbbf24' }}>{fmtAmountRaw(r.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 매체종류별 */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="section-title"><BarChart2 size={14} /> 매체종류별 집계</div>
                <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                  <table className="usage-table">
                    <thead><tr>
                      <th>매체 종류</th><th>집행건수</th><th>총 광고비</th>
                    </tr></thead>
                    <tbody>
                      {(summary.byType || []).map(r => (
                        <tr key={r.media_type || '미분류'}>
                          <td>{r.media_type || '미분류'}</td>
                          <td>{r.count}건</td>
                          <td style={{ color: '#fbbf24' }}>{fmtAmountRaw(r.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 매체별 상위 50 */}
              <div className="card">
                <div className="section-title"><Database size={14} /> 매체별 광고비 (상위 50)</div>
                <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                  <table className="usage-table">
                    <thead><tr>
                      <th>매체명</th><th>집행연도수</th><th>건수</th><th>첫해~마지막해</th><th>총 광고비</th><th>매체DB</th>
                    </tr></thead>
                    <tbody>
                      {(summary.byMedia || []).map(r => (
                        <tr key={r.media_name}>
                          <td style={{ fontWeight: 500 }}>{r.media_name}</td>
                          <td style={{ textAlign: 'center' }}>{r.year_count}년</td>
                          <td style={{ textAlign: 'center' }}>{r.record_count}</td>
                          <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{r.first_year}~{r.last_year}</td>
                          <td style={{ color: '#fbbf24' }}>{fmtAmountRaw(r.total_amount)}</td>
                          <td style={{ textAlign: 'center' }}>
                            {r.media_master_id
                              ? <span style={{ color: '#4ade80', fontSize: '0.75rem' }}>연결됨</span>
                              : <span style={{ color: '#475569', fontSize: '0.75rem' }}>미연결</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'detail' && (
        <div className="card">
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
            <input
              placeholder="매체명 검색"
              value={searchMedia}
              onChange={e => setSearchMedia(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #334155',
                background: '#0f172a', color: '#e2e8f0', fontSize: '0.85rem', flex: 1 }}
            />
          </div>
          {recLoading ? <div style={{ color: '#64748b' }}>로딩 중...</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="usage-table">
                <thead><tr>
                  <th>연도</th><th>매체명</th><th>광고내용</th><th>매체종류</th><th>광고비</th><th>회계일자</th>
                </tr></thead>
                <tbody>
                  {(records?.rows || []).map(r => (
                    <tr key={r.id}>
                      <td>{r.year}</td>
                      <td style={{ fontWeight: 500 }}>{r.media_name}</td>
                      <td style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{r.ad_content || '-'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{r.media_type || '-'}</td>
                      <td style={{ color: '#fbbf24' }}>{r.amount_krw ? `${r.amount_krw.toLocaleString()}천원` : '-'}</td>
                      <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{r.accounting_date || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {records?.total > 100 && (
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.5rem' }}>
                  총 {records.total}건 중 100건 표시
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'match' && <AdMatchPanel />}
    </div>
  );
}

function AdMatchPanel() {
  const qc = useQueryClient();
  const [batchSize, setBatchSize] = useState(5);
  const [selectedIds, setSelectedIds] = useState({}); // { ad_media_name: media_master_id }

  const { data: candidatesData, isLoading: candLoading, refetch: refetchCandidates } = useQuery({
    queryKey: ['ad-match-candidates', batchSize],
    queryFn: () => fetchAdMatchCandidates(batchSize),
  });

  const { data: matchedData, isLoading: matchedLoading } = useQuery({
    queryKey: ['ad-matched'],
    queryFn: fetchAdMatched,
  });

  const { data: mediaList } = useQuery({
    queryKey: ['media-simple'],
    queryFn: fetchMediaSimple,
  });

  const confirmMut = useMutation({
    mutationFn: ({ media_name, media_master_id }) => confirmAdMatch(media_name, media_master_id),
    onSuccess: () => {
      qc.invalidateQueries(['ad-match-candidates']);
      qc.invalidateQueries(['ad-matched']);
      qc.invalidateQueries(['ad-summary']);
      qc.invalidateQueries(['ad-media-names']);
    },
  });

  const rejectMut = useMutation({
    mutationFn: (media_name) => rejectAdMatch(media_name),
    onSuccess: () => {
      qc.invalidateQueries(['ad-match-candidates']);
    },
  });

  const candidates = candidatesData?.candidates || [];
  const mediaOptions = mediaList?.data || [];

  const handleConfirm = (cand) => {
    const media_master_id = selectedIds[cand.ad_media_name] || cand.ai_media_master_id;
    if (!media_master_id) return alert('연결할 매체를 선택하세요.');
    confirmMut.mutate({ media_name: cand.ad_media_name, media_master_id });
  };

  const fmt = (v) => v ? `${v.toLocaleString()}천원` : '-';

  return (
    <div>
      {/* 매칭 대기 목록 */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="section-title"><Database size={14} /> AI 매칭 후보</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0 1rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>한 번에</span>
          <select value={batchSize} onChange={e => setBatchSize(Number(e.target.value))}
            style={{ padding: '0.3rem 0.5rem', borderRadius: '6px', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155' }}>
            {[5,10,15,20].map(n => <option key={n} value={n}>{n}개</option>)}
          </select>
          <button onClick={() => refetchCandidates()} disabled={candLoading}
            style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: '#6366f1', color: '#e2e8f0', fontSize: '0.8rem' }}>새로 불러오기</button>
        </div>

        {candLoading ? <div style={{ color: '#64748b' }}>로딩 중...</div> : (
          candidates.length === 0 ? (
            <div style={{ color: '#64748b', padding: '1rem 0' }}>현재 매칭할 후보가 없습니다.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {candidates.map(c => (
                <div key={c.ad_media_name} style={{ padding: '0.75rem', borderRadius: '8px', background: '#0f172a', border: '1px solid #1e293b' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{c.ad_media_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>광고비 {fmt(c.total_amount)} · {c.record_count}건</div>
                    </div>
                    {c.ai_matched_standard && (
                      <div style={{ fontSize: '0.75rem', color: '#fbbf24', background: '#422006', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                        AI 추천: {c.ai_matched_standard}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>DB 후보:</span>
                    {c.db_candidates.slice(0, 4).map(n => (
                      <span key={n} style={{ fontSize: '0.72rem', color: '#94a3b8', background: '#1e293b', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{n}</span>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={selectedIds[c.ad_media_name] || c.ai_media_master_id || ''}
                      onChange={e => setSelectedIds(prev => ({ ...prev, [c.ad_media_name]: Number(e.target.value) }))}
                      style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', fontSize: '0.8rem', minWidth: '220px' }}
                    >
                      <option value="">매체 선택...</option>
                      {mediaOptions.map(m => <option key={m.id} value={m.id}>{m.standard_name}</option>)}
                    </select>
                    <button onClick={() => handleConfirm(c)} disabled={confirmMut.isLoading}
                      style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                        background: '#10b981', color: '#fff' }}>확인</button>
                    <button onClick={() => rejectMut.mutate(c.ad_media_name)} disabled={rejectMut.isLoading}
                      style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                        background: '#475569', color: '#e2e8f0' }}>거부</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* 매칭 현황 */}
      <div className="card">
        <div className="section-title"><CheckCircle size={14} /> 매칭 현황</div>
        {matchedLoading ? <div style={{ color: '#64748b' }}>로딩 중...</div> : (
          <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
            <table className="usage-table">
              <thead><tr>
                <th>광고 매체명</th><th>DB 표준명</th><th>매체종류</th><th>광고비</th><th>건수</th><th>집행기간</th>
              </tr></thead>
              <tbody>
                {(matchedData || []).map(r => (
                  <tr key={r.media_name}>
                    <td style={{ fontWeight: 500 }}>{r.media_name}</td>
                    <td>{r.standard_name}</td>
                    <td style={{ fontSize: '0.78rem' }}>{r.media_type || '-'}</td>
                    <td style={{ color: '#fbbf24' }}>{fmt(r.total_amount)}</td>
                    <td style={{ textAlign: 'center' }}>{r.record_count}</td>
                    <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{r.first_year}~{r.last_year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function HiddenMenuPanel() {
  const items = [
    { to: '/', icon: <LayoutDashboard size={16} />, label: '전체 뉴스', desc: 'RSS 수집 뉴스 목록' },
    { to: '/archive', icon: <CalendarDays size={16} />, label: '날짜별 조회', desc: '날짜별 기사 조회' },
    { to: '/bigkinds', icon: <Database size={16} />, label: '아카이브', desc: '빅카인즈 아카이브 검색' },
  ];
  return (
    <div className="card">
      <h2 className="section-title"><LayoutDashboard size={16} /> 숨겨진 메뉴</h2>
      <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
        메인 내비게이션에서 숨겨진 페이지입니다. 관리자만 접근할 수 있습니다.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {items.map(item => (
          <Link key={item.to} to={item.to} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#0f172a', borderRadius: '8px', border: '1px solid #1e293b', textDecoration: 'none', color: '#e2e8f0' }}>
            <span style={{ color: '#94a3b8' }}>{item.icon}</span>
            <div>
              <div style={{ fontWeight: 600 }}>{item.label}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{item.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: 'keywords', label: '키워드', icon: <Tag size={14} /> },
  { key: 'sources', label: 'RSS 소스', icon: <Rss size={14} /> },
  { key: 'users', label: '유저 관리', icon: <Users size={14} /> },
  { key: 'ai', label: 'AI 사용량', icon: <Cpu size={14} /> },
  { key: 'duplearn', label: '중복학습', icon: <BookX size={14} /> },
  { key: 'excluded', label: '배제 기사', icon: <Ban size={14} /> },
  { key: 'settings', label: '환경설정', icon: <Settings size={14} /> },
  { key: 'control', label: '수동제어', icon: <Shield size={14} /> },
  { key: 'system', label: '서버 현황', icon: <HardDrive size={14} /> },
  { key: 'telegram', label: '텔레그램', icon: <Bell size={14} /> },
  { key: 'hidden', label: '숨겨진 메뉴', icon: <LayoutDashboard size={14} /> },
  { key: 'ad', label: '광고비 분석', icon: <DollarSign size={14} /> },
];

export default function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'keywords';
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== tab) setTab(t);
  }, [searchParams]);

  const handleTab = (key) => {
    setTab(key);
    if (key === 'keywords') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', key);
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <Shield size={20} color="#a78bfa" />
          관리자 설정
        </h1>
      </div>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`admin-tab ${tab === t.key ? 'active' : ''}`} onClick={() => handleTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: '1rem' }}>
        {tab === 'keywords' && <KeywordsPanel />}
        {tab === 'sources' && <SourcesPanel />}
        {tab === 'users' && <UsersPanel />}
        {tab === 'ai' && <UsagePanel />}
        {tab === 'duplearn' && <DuplicateHintsPanel />}
        {tab === 'excluded' && <ExcludedPanel />}
        {tab === 'settings' && <SettingsPanel />}
        {tab === 'control' && <ControlPanel />}
        {tab === 'system' && <SystemPanel />}
        {tab === 'telegram' && <TelegramPanel />}
        {tab === 'hidden' && <HiddenMenuPanel />}
        {tab === 'ad' && <AdAnalysisPanel />}
      </div>

      <style>{`
        .admin-tabs {
          display: flex; gap: 0.25rem; flex-wrap: wrap;
          border-bottom: 1px solid #1e293b; padding-bottom: 0;
        }
        .admin-tab {
          display: flex; align-items: center; gap: 0.4rem;
          padding: 0.5rem 1rem; border: none; background: none;
          color: #64748b; font-size: 0.875rem; cursor: pointer;
          border-bottom: 2px solid transparent; transition: all 0.15s;
          margin-bottom: -1px;
        }
        .admin-tab:hover { color: #94a3b8; }
        .admin-tab.active { color: #a78bfa; border-bottom-color: #a78bfa; }

        .section-title {
          display: flex; align-items: center; gap: 0.5rem;
          font-size: 1rem; font-weight: 600; color: #f1f5f9; margin-bottom: 0.75rem;
        }
        .add-form { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        .kw-row {
          display: flex; align-items: center; gap: 0.6rem;
          padding: 0.5rem 0; border-bottom: 1px solid #1e293b;
        }
        .kw-row:last-child { border-bottom: none; }
        .kw-row.inactive { opacity: 0.5; }
        .weight-ctrl { display: flex; align-items: center; gap: 0.25rem; }
        .icon-btn {
          width: 28px; height: 28px; border-radius: 5px; border: none;
          background: #334155; color: #94a3b8; display: flex; align-items: center; justify-content: center;
          font-size: 0.9rem; transition: all 0.15s; flex-shrink: 0; cursor: pointer;
        }
        .icon-btn:hover { background: #475569; color: #e2e8f0; }
        .icon-btn.small { width: 22px; height: 22px; font-size: 0.85rem; }
        .icon-btn.danger:hover { background: #7f1d1d; color: #f87171; }

        .usage-summary {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 0.75rem; margin-bottom: 0.5rem;
        }
        .usage-card {
          background: #0f172a; border-radius: 8px; padding: 0.75rem; border: 1px solid #1e293b; text-align: center;
        }
        .usage-label { font-size: 0.72rem; color: #64748b; margin-bottom: 0.25rem; }
        .usage-value { font-size: 1.2rem; font-weight: 700; color: #e2e8f0; }
        .usage-value.cost { color: #fbbf24; }
        .usage-sub { font-size: 0.7rem; color: #475569; margin-top: 0.15rem; }
        .usage-table {
          width: 100%; border-collapse: collapse; font-size: 0.8rem;
        }
        .usage-table th {
          text-align: left; padding: 0.35rem 0.5rem;
          color: #64748b; font-weight: 600; border-bottom: 1px solid #1e293b; font-size: 0.75rem;
        }
        .usage-table td { padding: 0.35rem 0.5rem; border-bottom: 1px solid #0f172a; color: #cbd5e1; }
        .usage-table tr:last-child td { border-bottom: none; }
        .usage-table tr:hover td { background: #0f172a; }

        .settings-grid { display: flex; flex-direction: column; gap: 1.25rem; }
        .settings-section { padding: 0.75rem; background: #0f172a; border-radius: 8px; border: 1px solid #1e293b; }
        .settings-label { font-size: 0.875rem; font-weight: 600; color: #e2e8f0; margin-bottom: 0.25rem; }
        .settings-desc { font-size: 0.78rem; color: #64748b; }

        .empty-state { display: flex; align-items: center; justify-content: center; padding: 2rem; }

        .alert { padding: 0.6rem 0.9rem; border-radius: 7px; font-size: 0.85rem; display: flex; align-items: center; gap: 0.5rem; }
        .alert-success { background: #14532d; color: #4ade80; border: 1px solid #166534; }
        .alert-error { background: #7f1d1d; color: #f87171; border: 1px solid #991b1b; }
        .spin { animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
